import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone, MessageSquare, Video, Building2,
  User, Package, Wrench, Clock, Save, Plus, Trash2, X,
  Send, FileText, AlertTriangle, CheckCircle, ChevronDown, ChevronUp,
  Receipt, ClipboardList
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const statusLabels = {
  open: 'Ouvert', diagnosed: 'Diagnostiqué', waiting_parts: 'Attente pièces',
  in_repair: 'En réparation', ready: 'Prêt à rendre', closed: 'Clôturé'
};

const statusColors = {
  open: 'bg-[#2A1515] text-[#E74C3C]', diagnosed: 'bg-[#152A35] text-[#3498DB]',
  waiting_parts: 'bg-[#2A2010] text-[#F39C12]', in_repair: 'bg-[#2A2010] text-[#F39C12]',
  ready: 'bg-[#142915] text-[#00E01A]', closed: 'bg-[#2C3338] text-[#8C8F94]'
};

const EXCHANGE_TYPES = [
  { value: 'email_fournisseur', label: 'Email fournisseur', icon: Building2, color: '#8B5CF6' },
  { value: 'email_client', label: 'Email client', icon: Mail, color: '#3B82F6' },
  { value: 'appel_fournisseur', label: 'Appel fournisseur', icon: Phone, color: '#8B5CF6' },
  { value: 'appel_client', label: 'Appel client', icon: Phone, color: '#3B82F6' },
  { value: 'interne', label: 'Note interne', icon: MessageSquare, color: '#F59E0B' },
];

const OP_TYPES = [
  { value: 'diagnostic', label: 'Diagnostic' },
  { value: 'intervention', label: 'Intervention' },
  { value: 'test', label: 'Test / Essai' },
  { value: 'controle', label: 'Contrôle qualité' },
];

function getExchangeTypeInfo(type) {
  return EXCHANGE_TYPES.find(t => t.value === type) || EXCHANGE_TYPES[4];
}

// ========== WORKFLOW STEPS ==========
function WorkflowSteps({ currentStatus, onStatusChange }) {
  const steps = ['open', 'diagnosed', 'waiting_parts', 'in_repair', 'ready', 'closed'];
  const currentIndex = steps.indexOf(currentStatus);

  return (
    <div className="flex flex-wrap gap-2" data-testid="workflow-steps">
      {steps.map((step, idx) => (
        <button
          key={step}
          onClick={() => onStatusChange(step)}
          disabled={idx > currentIndex + 1}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            step === currentStatus
              ? 'bg-[#00E01A] text-black font-bold'
              : idx < currentIndex
              ? 'bg-[#142915] text-[#00E01A] border border-[#00E01A]/30'
              : idx === currentIndex + 1
              ? 'bg-[#1D2327] text-white border border-[#2C3338] hover:border-[#00E01A]/50 cursor-pointer'
              : 'bg-[#1D2327] text-[#52555A] border border-[#2C3338] cursor-not-allowed'
          }`}
          data-testid={`workflow-step-${step}`}
        >
          {statusLabels[step]}
        </button>
      ))}
    </div>
  );
}

// ========== EXCHANGE CARD ==========
function SAVExchangeCard({ exchange, onDelete }) {
  const [collapsed, setCollapsed] = useState(false);
  const typeInfo = getExchangeTypeInfo(exchange.type);
  const Icon = typeInfo.icon;

  return (
    <div
      className="rounded-lg overflow-hidden border border-[#2C3338]"
      style={{ borderLeftWidth: 3, borderLeftColor: typeInfo.color }}
      data-testid={`sav-exchange-${exchange.id}`}
    >
      <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#1a1a1a] transition-colors" onClick={() => setCollapsed(!collapsed)}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: typeInfo.color }}>
          <Icon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm font-medium truncate">{exchange.auteur}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded text-[#8C8F94] bg-[#1D2327]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {typeInfo.label}
            </span>
          </div>
          {exchange.sujet && <p className="text-[#8C8F94] text-xs truncate">{exchange.sujet}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[#8C8F94] text-xs">{exchange.date ? new Date(exchange.date + 'T00:00:00').toLocaleDateString('fr-FR') : ''}</span>
          <button onClick={(e) => { e.stopPropagation(); onDelete(exchange.id); }} className="text-[#8C8F94] hover:text-[#EF4444] transition-colors" data-testid={`del-sav-exch-${exchange.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {!collapsed && (
        <div className="px-3 pb-3 ml-11">
          {exchange.destinataire && <p className="text-[#8C8F94] text-xs mb-2">À : {exchange.destinataire}</p>}
          <pre className="text-[#ccc] text-sm whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "'Barlow', sans-serif" }}>{exchange.contenu}</pre>
        </div>
      )}
    </div>
  );
}

// ========== OPERATION CARD ==========
function OperationCard({ op, onDelete }) {
  const opType = OP_TYPES.find(t => t.value === op.type) || OP_TYPES[0];
  const typeColors = { diagnostic: '#3B82F6', intervention: '#F59E0B', test: '#10B981', controle: '#8B5CF6' };

  return (
    <div className="bg-[#1D2327] rounded-lg border border-[#2C3338] p-4" data-testid={`sav-op-${op.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{ backgroundColor: typeColors[op.type] + '20', color: typeColors[op.type] }}>
            {opType.label}
          </span>
          <span className="text-[#8C8F94] text-xs">{op.date ? new Date(op.date + 'T00:00:00').toLocaleDateString('fr-FR') : ''}</span>
        </div>
        <button onClick={() => onDelete(op.id)} className="text-[#8C8F94] hover:text-[#EF4444] transition-colors" data-testid={`del-sav-op-${op.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
      <p className="text-white text-sm mb-1">{op.description}</p>
      <div className="flex gap-4 text-xs text-[#8C8F94]">
        <span>Technicien : {op.technicien}</span>
        {op.duree_minutes && <span>Durée : {op.duree_minutes} min</span>}
      </div>
      {op.pieces_utilisees && <p className="text-[#8C8F94] text-xs mt-1">Pièces : {op.pieces_utilisees}</p>}
    </div>
  );
}

// ========== MAIN COMPONENT ==========
export default function SAVTicketDetail() {
  const { api, user } = useAuth();
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  // Exchange form
  const [showExchangeForm, setShowExchangeForm] = useState(false);
  const [exchForm, setExchForm] = useState({
    type: 'email_fournisseur', date: new Date().toISOString().split('T')[0],
    auteur: '', destinataire: '', sujet: '', contenu: ''
  });

  // Operation form
  const [showOpForm, setShowOpForm] = useState(false);
  const [opForm, setOpForm] = useState({
    type: 'diagnostic', date: new Date().toISOString().split('T')[0],
    technicien: '', description: '', duree_minutes: '', pieces_utilisees: ''
  });

  // Billing form
  const [billing, setBilling] = useState({
    labor_hours: 0, labor_rate: 85, labor_cost: 0, parts_cost: 0,
    discount_percent: 0, notes: '', warranty_covered: false,
  });

  const fetchTicket = useCallback(async () => {
    try {
      const res = await api.get(`/sav/${ticketId}`);
      setTicket(res.data);
      if (res.data.billing) setBilling(res.data.billing);
      // Set default author
      if (user) {
        setExchForm(p => ({...p, auteur: `${user.first_name} ${user.last_name}`}));
        setOpForm(p => ({...p, technicien: `${user.first_name} ${user.last_name}`}));
      }
    } catch (e) {
      toast.error("Ticket non trouvé");
    } finally {
      setLoading(false);
    }
  }, [api, ticketId, user]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  const handleStatusChange = async (newStatus) => {
    try {
      await api.patch(`/sav/${ticketId}/status?status=${newStatus}`);
      toast.success(`Statut mis à jour : ${statusLabels[newStatus]}`);
      fetchTicket();
    } catch (e) {
      toast.error("Erreur mise à jour statut");
    }
  };

  const handleAddExchange = async () => {
    if (!exchForm.contenu.trim()) { toast.error("Le contenu est requis"); return; }
    try {
      await api.post(`/sav/${ticketId}/exchanges`, exchForm);
      toast.success("Échange ajouté");
      setShowExchangeForm(false);
      setExchForm(p => ({...p, destinataire: '', sujet: '', contenu: ''}));
      fetchTicket();
    } catch (e) { toast.error("Erreur ajout échange"); }
  };

  const handleDeleteExchange = async (exchangeId) => {
    try {
      await api.delete(`/sav/${ticketId}/exchanges/${exchangeId}`);
      toast.success("Échange supprimé");
      fetchTicket();
    } catch (e) { toast.error("Erreur suppression"); }
  };

  const handleAddOperation = async () => {
    if (!opForm.description.trim()) { toast.error("La description est requise"); return; }
    try {
      await api.post(`/sav/${ticketId}/operations`, {
        ...opForm,
        duree_minutes: opForm.duree_minutes ? parseInt(opForm.duree_minutes) : null,
      });
      toast.success("Opération ajoutée");
      setShowOpForm(false);
      setOpForm(p => ({...p, description: '', duree_minutes: '', pieces_utilisees: ''}));
      fetchTicket();
    } catch (e) { toast.error("Erreur ajout opération"); }
  };

  const handleDeleteOperation = async (opId) => {
    try {
      await api.delete(`/sav/${ticketId}/operations/${opId}`);
      toast.success("Opération supprimée");
      fetchTicket();
    } catch (e) { toast.error("Erreur suppression"); }
  };

  const handleSaveBilling = async () => {
    try {
      const res = await api.put(`/sav/${ticketId}/billing`, billing);
      setBilling(res.data);
      toast.success("Facturation enregistrée");
      fetchTicket();
    } catch (e) { toast.error("Erreur facturation"); }
  };

  const handleAddNote = async (noteText) => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/sav/${ticketId}/notes?note=${encodeURIComponent(noteText)}`);
      toast.success("Note ajoutée");
      fetchTicket();
    } catch (e) { toast.error("Erreur ajout note"); }
  };

  const inputClass = "w-full bg-[#141617] border border-[#2C3338] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00E01A] focus:outline-none transition-colors";
  const labelClass = "text-[#8C8F94] text-xs uppercase tracking-wider mb-1 block";

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-6 text-center">
        <Wrench size={48} className="mx-auto text-[#8C8F94] mb-4" />
        <p className="text-[#8C8F94]">Ticket non trouvé</p>
        <Link to="/sav" className="text-[#00E01A] hover:underline mt-2 inline-block">Retour au SAV</Link>
      </div>
    );
  }

  const tabs = [
    { id: 'info', label: 'Informations', icon: FileText },
    { id: 'exchanges', label: 'Échanges', icon: Mail, count: (ticket.exchanges || []).length },
    { id: 'operations', label: 'Opérations', icon: ClipboardList, count: (ticket.operations || []).length },
    { id: 'billing', label: 'Facturation', icon: Receipt },
  ];

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="sav-ticket-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/sav" className="p-2 rounded-lg bg-[#1D2327] border border-[#2C3338] text-[#8C8F94] hover:text-white hover:border-[#00E01A]/30 transition-colors" data-testid="back-to-sav">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">{ticket.ticket_number}</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusColors[ticket.status]}`}>{statusLabels[ticket.status]}</span>
          </div>
          <p className="text-[#8C8F94] text-sm mt-0.5">
            {ticket.client_name} {ticket.product_name ? `— ${ticket.product_name}` : ''}
          </p>
        </div>
      </div>

      {/* Workflow */}
      <WorkflowSteps currentStatus={ticket.status} onStatusChange={handleStatusChange} />

      {/* Tabs */}
      <div className="flex gap-1 bg-[#111111] rounded-lg p-1" data-testid="sav-detail-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 flex-1 py-2.5 px-3 rounded-lg text-xs font-semibold transition-all ${
              activeTab === tab.id ? 'bg-[#00E01A] text-black' : 'text-[#8C8F94] hover:text-white'
            }`}
            data-testid={`sav-tab-${tab.id}`}
          >
            <tab.icon size={14} />
            <span className="hidden sm:inline">{tab.label}</span>
            {tab.count > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                activeTab === tab.id ? 'bg-black/20 text-black' : 'bg-[#2C3338] text-[#8C8F94]'
              }`}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && <InfoTab ticket={ticket} onAddNote={handleAddNote} />}
      {activeTab === 'exchanges' && (
        <ExchangesTab
          exchanges={ticket.exchanges || []}
          showForm={showExchangeForm}
          setShowForm={setShowExchangeForm}
          form={exchForm}
          setForm={setExchForm}
          onAdd={handleAddExchange}
          onDelete={handleDeleteExchange}
          inputClass={inputClass}
          labelClass={labelClass}
        />
      )}
      {activeTab === 'operations' && (
        <OperationsTab
          operations={ticket.operations || []}
          showForm={showOpForm}
          setShowForm={setShowOpForm}
          form={opForm}
          setForm={setOpForm}
          onAdd={handleAddOperation}
          onDelete={handleDeleteOperation}
          inputClass={inputClass}
          labelClass={labelClass}
        />
      )}
      {activeTab === 'billing' && (
        <BillingTab
          billing={billing}
          setBilling={setBilling}
          ticket={ticket}
          onSave={handleSaveBilling}
          inputClass={inputClass}
          labelClass={labelClass}
        />
      )}
    </div>
  );
}

// ========== INFO TAB ==========
function InfoTab({ ticket, onAddNote }) {
  const [noteText, setNoteText] = useState('');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="sav-info-tab">
      {/* Details */}
      <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5 space-y-4">
        <h3 className="text-white font-semibold">Détails du ticket</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <User size={16} className="text-[#8C8F94]" />
            <div>
              <p className="text-[#8C8F94] text-xs">Client</p>
              <Link to={`/clients/${ticket.client_id}`} className="text-white hover:text-[#00E01A] transition-colors">{ticket.client_name}</Link>
            </div>
          </div>
          {ticket.product_name && (
            <div className="flex items-center gap-3">
              <Package size={16} className="text-[#8C8F94]" />
              <div>
                <p className="text-[#8C8F94] text-xs">Produit</p>
                <p className="text-white">{ticket.product_name}</p>
              </div>
            </div>
          )}
          {ticket.equipment_serial && (
            <div className="flex items-center gap-3">
              <FileText size={16} className="text-[#8C8F94]" />
              <div>
                <p className="text-[#8C8F94] text-xs">N° série</p>
                <p className="text-white font-mono">{ticket.equipment_serial}</p>
              </div>
            </div>
          )}
          {ticket.supplier_name && (
            <div className="flex items-center gap-3">
              <Building2 size={16} className="text-[#8C8F94]" />
              <div>
                <p className="text-[#8C8F94] text-xs">Fournisseur</p>
                <p className="text-white">{ticket.supplier_name}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-[#8C8F94]" />
            <div>
              <p className="text-[#8C8F94] text-xs">Créé le</p>
              <p className="text-white">{new Date(ticket.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
          <div>
            <p className="text-[#8C8F94] text-xs mb-1">Garantie à l'ouverture</p>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              ticket.warranty_status_at_open === 'active' ? 'bg-[#142915] text-[#00E01A]' :
              ticket.warranty_status_at_open === 'expired' ? 'bg-[#2A1515] text-[#E74C3C]' :
              'bg-[#2C3338] text-[#8C8F94]'
            }`}>
              {ticket.warranty_status_at_open === 'active' ? 'Sous garantie' : ticket.warranty_status_at_open === 'expired' ? 'Hors garantie' : 'Inconnue'}
            </span>
          </div>
        </div>

        <div className="pt-4 border-t border-[#2C3338]">
          <p className="text-[#8C8F94] text-xs mb-2">Description du problème</p>
          <p className="text-white text-sm whitespace-pre-wrap">{ticket.description}</p>
        </div>
      </div>

      {/* Notes & History */}
      <div className="space-y-4">
        {/* Notes */}
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
          <h3 className="text-white font-semibold mb-3">Notes ({(ticket.notes || []).length})</h3>
          <div className="space-y-2 max-h-[200px] overflow-y-auto mb-3">
            {(ticket.notes || []).length === 0 ? (
              <p className="text-[#8C8F94] text-sm">Aucune note</p>
            ) : (
              (ticket.notes || []).map(note => (
                <div key={note.id} className="bg-[#141617] rounded-lg p-3">
                  <p className="text-white text-sm">{note.text}</p>
                  <p className="text-[#52555A] text-xs mt-1">{note.user_name} — {new Date(note.timestamp).toLocaleString('fr-FR')}</p>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2">
            <input
              className="flex-1 bg-[#141617] border border-[#2C3338] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00E01A] focus:outline-none"
              placeholder="Ajouter une note..."
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && noteText.trim()) { onAddNote(noteText); setNoteText(''); }}}
              data-testid="sav-note-input"
            />
            <button
              onClick={() => { if (noteText.trim()) { onAddNote(noteText); setNoteText(''); }}}
              className="px-3 py-2 bg-[#00E01A] text-black rounded-lg hover:bg-[#00B815] transition-colors"
              data-testid="sav-add-note-btn"
            >
              <Send size={14} />
            </button>
          </div>
        </div>

        {/* Status History */}
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
          <h3 className="text-white font-semibold mb-3">Historique des statuts</h3>
          <div className="space-y-2">
            {(ticket.status_history || []).map((entry, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#00E01A]" />
                <div className="flex-1">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${statusColors[entry.status]}`}>{statusLabels[entry.status]}</span>
                  <span className="text-[#8C8F94] text-xs ml-2">{entry.user_name}</span>
                </div>
                <span className="text-[#52555A] text-xs">{new Date(entry.timestamp).toLocaleString('fr-FR')}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Parts */}
        {(ticket.parts || []).length > 0 && (
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
            <h3 className="text-white font-semibold mb-3">Pièces ({ticket.parts.length})</h3>
            <div className="space-y-2">
              {ticket.parts.map(part => (
                <div key={part.id} className="bg-[#141617] rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm">{part.designation}</p>
                    {part.reference && <p className="text-[#8C8F94] text-xs font-mono">{part.reference}</p>}
                  </div>
                  <div className="text-right">
                    {part.cost && <p className="text-white text-sm font-semibold">{part.cost}€</p>}
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      part.status === 'installed' ? 'bg-[#142915] text-[#00E01A]' :
                      part.status === 'received' ? 'bg-[#152A35] text-[#3498DB]' :
                      'bg-[#2A2010] text-[#F39C12]'
                    }`}>
                      {part.status === 'installed' ? 'Installée' : part.status === 'received' ? 'Reçue' : 'Commandée'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ========== EXCHANGES TAB ==========
function ExchangesTab({ exchanges, showForm, setShowForm, form, setForm, onAdd, onDelete, inputClass, labelClass }) {
  return (
    <div className="space-y-3" data-testid="sav-exchanges-tab">
      {exchanges.map(ex => (
        <SAVExchangeCard key={ex.id} exchange={ex} onDelete={onDelete} />
      ))}

      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="w-full border border-dashed border-[#2C3338] rounded-lg p-3 text-[#8C8F94] hover:text-[#00E01A] hover:border-[#00E01A] transition-colors flex items-center justify-center gap-2"
          data-testid="add-sav-exchange-btn"
        >
          <Plus size={16} /> Ajouter un échange
        </button>
      ) : (
        <div className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-semibold text-sm">Nouvel échange</h4>
            <button onClick={() => setShowForm(false)} className="text-[#8C8F94] hover:text-white"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Type</label>
              <select className={inputClass} value={form.type} onChange={e => setForm(p => ({...p, type: e.target.value}))} data-testid="sav-exch-type">
                {EXCHANGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input className={inputClass} type="date" value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} data-testid="sav-exch-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Auteur</label>
              <input className={inputClass} value={form.auteur} onChange={e => setForm(p => ({...p, auteur: e.target.value}))} data-testid="sav-exch-auteur" />
            </div>
            <div>
              <label className={labelClass}>Destinataire</label>
              <input className={inputClass} value={form.destinataire} onChange={e => setForm(p => ({...p, destinataire: e.target.value}))} data-testid="sav-exch-dest" />
            </div>
          </div>
          <div>
            <label className={labelClass}>Sujet</label>
            <input className={inputClass} value={form.sujet} onChange={e => setForm(p => ({...p, sujet: e.target.value}))} data-testid="sav-exch-sujet" />
          </div>
          <div>
            <label className={labelClass}>Contenu</label>
            <textarea className={inputClass + " min-h-[100px]"} value={form.contenu} onChange={e => setForm(p => ({...p, contenu: e.target.value}))} data-testid="sav-exch-contenu" />
          </div>
          <button onClick={onAdd} className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-5 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]" data-testid="submit-sav-exchange-btn">
            <Send size={16} /> Ajouter
          </button>
        </div>
      )}
    </div>
  );
}

// ========== OPERATIONS TAB ==========
function OperationsTab({ operations, showForm, setShowForm, form, setForm, onAdd, onDelete, inputClass, labelClass }) {
  return (
    <div className="space-y-3" data-testid="sav-operations-tab">
      {operations.map(op => (
        <OperationCard key={op.id} op={op} onDelete={onDelete} />
      ))}

      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="w-full border border-dashed border-[#2C3338] rounded-lg p-3 text-[#8C8F94] hover:text-[#00E01A] hover:border-[#00E01A] transition-colors flex items-center justify-center gap-2"
          data-testid="add-sav-op-btn"
        >
          <Plus size={16} /> Ajouter une opération
        </button>
      ) : (
        <div className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-semibold text-sm">Nouvelle opération technique</h4>
            <button onClick={() => setShowForm(false)} className="text-[#8C8F94] hover:text-white"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Type</label>
              <select className={inputClass} value={form.type} onChange={e => setForm(p => ({...p, type: e.target.value}))} data-testid="sav-op-type">
                {OP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input className={inputClass} type="date" value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} data-testid="sav-op-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Technicien</label>
              <input className={inputClass} value={form.technicien} onChange={e => setForm(p => ({...p, technicien: e.target.value}))} data-testid="sav-op-tech" />
            </div>
            <div>
              <label className={labelClass}>Durée (min)</label>
              <input className={inputClass} type="number" value={form.duree_minutes} onChange={e => setForm(p => ({...p, duree_minutes: e.target.value}))} data-testid="sav-op-duree" />
            </div>
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea className={inputClass + " min-h-[80px]"} value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} data-testid="sav-op-desc" />
          </div>
          <div>
            <label className={labelClass}>Pièces utilisées</label>
            <input className={inputClass} value={form.pieces_utilisees} onChange={e => setForm(p => ({...p, pieces_utilisees: e.target.value}))} placeholder="Ex: Batterie eFoil, câble moteur..." data-testid="sav-op-pieces" />
          </div>
          <button onClick={onAdd} className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-5 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]" data-testid="submit-sav-op-btn">
            <Plus size={16} /> Ajouter l'opération
          </button>
        </div>
      )}
    </div>
  );
}

// ========== BILLING TAB ==========
function BillingTab({ billing, setBilling, ticket, onSave, inputClass, labelClass }) {
  const { api } = useAuth();
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const laborTotal = (billing.labor_hours || 0) * (billing.labor_rate || 0);
  const subtotal = laborTotal + (billing.parts_cost || 0);
  const discount = subtotal * (billing.discount_percent || 0) / 100;
  const totalHT = subtotal - discount;
  const totalTTC = totalHT * 1.2;

  const hasAxonautInvoice = billing.axonaut_invoice_id || billing.axonaut_invoice_number;

  const handleCreateAxonautInvoice = async () => {
    setCreatingInvoice(true);
    try {
      const res = await api.post(`/sav/${ticket.id}/axonaut-invoice`);
      toast.success(res.data.message || 'Facture Axonaut créée');
      setBilling(prev => ({
        ...prev,
        axonaut_invoice_id: res.data.axonaut_invoice_id,
        axonaut_invoice_number: res.data.axonaut_invoice_number,
      }));
    } catch (e) {
      const detail = e.response?.data?.detail || 'Erreur création facture Axonaut';
      toast.error(detail);
    } finally {
      setCreatingInvoice(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="sav-billing-tab">
      <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
        <h3 className="text-white font-semibold mb-4">Facturation du ticket</h3>

        {billing.warranty_covered && (
          <div className="mb-4 p-3 bg-[#142915] border border-[#00E01A]/20 rounded-lg flex items-center gap-2 text-[#00E01A] text-sm">
            <CheckCircle size={16} /> Pris en charge sous garantie
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelClass}>Heures de main d'œuvre</label>
            <input className={inputClass} type="number" step="0.5" value={billing.labor_hours || ''} onChange={e => setBilling(p => ({...p, labor_hours: parseFloat(e.target.value) || 0}))} data-testid="billing-labor-hours" />
          </div>
          <div>
            <label className={labelClass}>Tarif horaire (€/h)</label>
            <input className={inputClass} type="number" value={billing.labor_rate || ''} onChange={e => setBilling(p => ({...p, labor_rate: parseFloat(e.target.value) || 0}))} data-testid="billing-labor-rate" />
          </div>
          <div>
            <label className={labelClass}>Coût des pièces (€)</label>
            <input className={inputClass} type="number" step="0.01" value={billing.parts_cost || ''} onChange={e => setBilling(p => ({...p, parts_cost: parseFloat(e.target.value) || 0}))} data-testid="billing-parts-cost" />
          </div>
          <div>
            <label className={labelClass}>Remise (%)</label>
            <input className={inputClass} type="number" value={billing.discount_percent || ''} onChange={e => setBilling(p => ({...p, discount_percent: parseFloat(e.target.value) || 0}))} data-testid="billing-discount" />
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            checked={billing.warranty_covered}
            onChange={e => setBilling(p => ({...p, warranty_covered: e.target.checked}))}
            className="rounded border-[#2C3338]"
            data-testid="billing-warranty-check"
          />
          <label className="text-[#8C8F94] text-sm">Pris en charge sous garantie</label>
        </div>

        <div>
          <label className={labelClass}>Notes de facturation</label>
          <textarea className={inputClass + " min-h-[60px]"} value={billing.notes || ''} onChange={e => setBilling(p => ({...p, notes: e.target.value}))} data-testid="billing-notes" />
        </div>

        {/* Totals */}
        <div className="mt-4 pt-4 border-t border-[#2C3338] space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-[#8C8F94]">Main d'œuvre ({billing.labor_hours || 0}h x {billing.labor_rate || 0}€)</span>
            <span className="text-white">{laborTotal.toFixed(2)}€</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#8C8F94]">Pièces</span>
            <span className="text-white">{(billing.parts_cost || 0).toFixed(2)}€</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#8C8F94]">Sous-total HT</span>
            <span className="text-white">{subtotal.toFixed(2)}€</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-[#8C8F94]">Remise ({billing.discount_percent}%)</span>
              <span className="text-[#00E01A]">-{discount.toFixed(2)}€</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-semibold pt-2 border-t border-[#2C3338]">
            <span className="text-[#8C8F94]">Total HT</span>
            <span className="text-white">{totalHT.toFixed(2)}€</span>
          </div>
          <div className="flex justify-between text-lg font-bold">
            <span className="text-white">Total TTC</span>
            <span className="text-[#00E01A]">{totalTTC.toFixed(2)}€</span>
          </div>
        </div>

        <button
          onClick={onSave}
          className="mt-4 flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-5 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]"
          data-testid="save-billing-btn"
        >
          <Save size={16} /> Enregistrer la facturation
        </button>

        {/* Axonaut invoice creation */}
        <div className="mt-4 pt-4 border-t border-[#2C3338]">
          {hasAxonautInvoice ? (
            <div className="flex items-center gap-3 p-3 bg-[#142915] border border-[#00E01A]/20 rounded-lg" data-testid="axonaut-invoice-ref">
              <CheckCircle size={18} className="text-[#00E01A]" />
              <div>
                <p className="text-[#00E01A] text-sm font-semibold">Facture Axonaut créée</p>
                <p className="text-[#8C8F94] text-xs">N° {billing.axonaut_invoice_number || billing.axonaut_invoice_id}</p>
              </div>
            </div>
          ) : (
            <button
              onClick={handleCreateAxonautInvoice}
              disabled={creatingInvoice || totalHT === 0}
              className="flex items-center gap-2 bg-[#152A35] hover:bg-[#1a3545] text-[#3B82F6] border border-[#3B82F6]/30 font-semibold px-5 py-2.5 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="create-axonaut-invoice-btn"
            >
              <Receipt size={16} />
              {creatingInvoice ? 'Création en cours...' : 'Générer la facture Axonaut'}
            </button>
          )}
          {!hasAxonautInvoice && totalHT === 0 && (
            <p className="text-[#8C8F94] text-xs mt-2">Enregistrez la facturation d'abord (total &gt; 0€)</p>
          )}
        </div>
      </div>
    </div>
  );
}
